import type { PaymentMethodCard } from '@/api/types'

/** Pure rules behind the Payment methods card (BI11, BI12). */

const BRANDS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
}

export function brandLabel(brand: string): string {
  return BRANDS[brand.toLowerCase()] ?? (brand === '' ? 'Card' : brand.charAt(0).toUpperCase() + brand.slice(1))
}

export function cardLabel(card: Pick<PaymentMethodCard, 'brand' | 'last4'>): string {
  return `${brandLabel(card.brand)} •••• ${card.last4}`
}

export function expiryLabel(card: Pick<PaymentMethodCard, 'exp_month' | 'exp_year'>): string {
  return `${String(card.exp_month).padStart(2, '0')}/${String(card.exp_year).slice(-2)}`
}

/** A card is expired from the first day of the month after its expiry month. */
export function isExpired(card: Pick<PaymentMethodCard, 'exp_month' | 'exp_year'>, now: Date = new Date()): boolean {
  return card.exp_year < now.getFullYear() || (card.exp_year === now.getFullYear() && card.exp_month < now.getMonth() + 1)
}

export type CardListState = 'empty' | 'single' | 'many'

export function cardListState(cards: readonly PaymentMethodCard[]): CardListState {
  return cards.length === 0 ? 'empty' : cards.length === 1 ? 'single' : 'many'
}

/** The default card first, then the rest newest first. */
export function orderedCards(cards: readonly PaymentMethodCard[]): PaymentMethodCard[] {
  return [...cards].sort((a, b) => {
    if (a.is_default !== b.is_default) return a.is_default ? -1 : 1
    return (b.created ?? '').localeCompare(a.created ?? '')
  })
}

/**
 * The only card cannot be removed while something bills it — a renewing subscription, a scheduled plan
 * change, an unpaid invoice, or auto-recharge; the server (409) is the authority (BI12, bug-222).
 */
export function removeHint(cards: readonly PaymentMethodCard[]): string | null {
  return cards.length <= 1
    ? 'This is your only card. While a subscription renews, a plan change is scheduled or auto-recharge is on, it cannot be removed — add another card first.'
    : null
}

/**
 * What the remove dialog says about the card being removed (bug-222). Removing the LAST card is
 * allowed only when nothing bills it, and then the person must be told what stops working.
 */
export function removeDialogText(removing: Pick<PaymentMethodCard, 'is_default'>, cardCount: number): string {
  if (cardCount <= 1) {
    return 'This is your only card. If the subscription or auto-recharge still uses it, removal is refused. Otherwise, once it is gone: auto-recharge cannot be switched on, police balance will not refill by itself, and buying a plan or a top-up will ask for a card again.'
  }
  return removing.is_default
    ? 'This is your default card. The most recently added other card becomes the default.'
    : 'The card is detached from your account.'
}

export function cardErrorText(code: string | undefined, message: string): string {
  switch (code) {
    case 'last_payment_method':
      // The server's sentence names what still uses the card (bug-222); keep it when present.
      return message.trim().length > 0
        ? message
        : 'You cannot remove your last card while a subscription, a scheduled plan change or auto-recharge depends on it. Add another card first.'
    default:
      return message
  }
}
